import React, { useState, useEffect, useRef } from 'react';

interface ContactInfo {
  email?: string;
  phone?: string;
  whatsapp?: string;
  social_media?: string;
  social_handles?: Record<string, string>;
  profile_url?: string;
  profile_image_url?: string;
  website?: string;
  bio?: string;
  company?: string;
  role?: string;
  followers?: number;
  verified?: boolean;
  source?: string;
}

interface TargetLocation {
  city?: string;
  neighborhood?: string;
  country?: string;
}

interface BudgetRange {
  min?: number;
  max?: number;
  currency?: string;
}

interface Lead {
  id: string;
  external_id: string;
  source_platform: string;
  source_url: string | null;
  original_content: string;
  original_language?: string | null;
  english_translation?: string | null;
  detected_at: string;
  classification: string;
  confidence_score: number;
  status: string;
  name: string | null;
  contact_info: ContactInfo | null;
  intent_type: string | null;
  stay_duration: string | null;
  target_location: TargetLocation | null;
  property_preferences: Record<string, any> | null;
  budget_range: BudgetRange | null;
  timeline: string | null;
  special_requirements: string[] | null;
  slack_notification_sent?: boolean;
  slack_notification_sent_at?: string | null;
  enrichment_status?: string | null;
  enrichment_stage?: string | null;
  enriched_at?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

interface LeadDetailPanelProps {
  lead: Lead | null;
  onClose: () => void;
  onUpdate?: (lead: Lead) => void;
}

const STAGE_PCT: Record<string, number> = {
  queued: 10,
  'analyzing post': 25,
  'checking website': 40,
  'searching platforms': 65,
  'extracting contacts': 85,
  done: 100,
};

const LeadDetailPanel: React.FC<LeadDetailPanelProps> = ({ lead, onClose, onUpdate }) => {
  // Hooks must run before any early return.
  const [enriching, setEnriching] = useState(false);
  const [flash, setFlash] = useState(false);
  const prevStatus = useRef<string | null | undefined>(undefined);

  // Flash the contact card green when enrichment transitions to done.
  useEffect(() => {
    const status = lead?.enrichment_status;
    if (prevStatus.current && prevStatus.current !== 'done' && status === 'done') {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 2500);
      prevStatus.current = status;
      return () => clearTimeout(t);
    }
    prevStatus.current = status;
  }, [lead?.id, lead?.enrichment_status]);

  if (!lead) return null;

  const isEnriching = lead.enrichment_status === 'pending' || lead.enrichment_status === 'running';
  const stagePct = STAGE_PCT[lead.enrichment_stage || 'queued'] ?? 20;

  // Trigger a (re-)enrichment: queue it server-side and let the parent's poll
  // drive the progress bar. No reload, no closing the slider.
  const handleGetContact = async () => {
    setEnriching(true);
    try {
      const response = await fetch(`/api/v1/leads/${lead.id}/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const updated = await response.json();
        if (onUpdate && updated?.id) onUpdate(updated);
      } else {
        const error = await response.json().catch(() => ({}));
        alert(`Failed to start enrichment: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error enriching contact:', error);
      alert('Error starting enrichment. Check if the API server is running.');
    } finally {
      setEnriching(false);
    }
  };


  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'potential': return 'bg-yellow-100 text-yellow-800';
      case 'irrelevant': return 'bg-red-100 text-red-800';
      case 'disqualified': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'detected': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'engaged': return 'bg-indigo-100 text-indigo-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleReject = async () => {
    if (!confirm('Are you sure you want to reject this lead?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/leads/${lead.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        alert('Lead rejected successfully');
        onClose();
        // Reload page to see updated status
        window.location.reload();
      } else {
        alert('Failed to reject lead');
      }
    } catch (error) {
      console.error('Error rejecting lead:', error);
      alert('Error rejecting lead');
    }
  };

  const handleSendToMattermost = async () => {
    // Check if already notified and confirm resend
    if (lead.slack_notification_sent) {
      const confirmResend = window.confirm(
        'A Mattermost notification was already sent for this lead.\n\nDo you want to send another notification?'
      );
      if (!confirmResend) return;
    }

    try {
      const response = await fetch(`/api/v1/leads/${lead.id}/notify-slack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        const resendNote = result.was_resend ? '\n\nNote: This was a re-send (notification already sent before)' : '';
        alert(`Notification sent to Mattermost successfully!\n\nClassification: ${result.classification}\nConfidence: ${result.confidence_score}%${resendNote}`);
      } else {
        const error = await response.json();
        alert(`Failed to send to Mattermost: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending to Mattermost:', error);
      alert('Error sending notification to Mattermost. Check if API server is running.');
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'reddit': return '🔴';
      case 'telegram': return '✈️';
      case 'twitter': return '🐦';
      case 'facebook': return '📘';
      default: return '🌐';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLanguageName = (code: string): string => {
    const languageNames: Record<string, string> = {
      'en': 'English',
      'ar': 'Arabic',
      'ru': 'Russian',
      'hi': 'Hindi',
      'ur': 'Urdu',
      'fa': 'Persian/Farsi',
      'zh': 'Chinese',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'tr': 'Turkish',
      'ta': 'Tamil',
      'te': 'Telugu',
      'bn': 'Bengali',
      'pa': 'Punjabi',
      'fil': 'Filipino/Tagalog',
      'tl': 'Tagalog',
      'th': 'Thai',
      'vi': 'Vietnamese',
      'ko': 'Korean',
      'ja': 'Japanese',
    };
    return languageNames[code] || code;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Side Panel */}
      <div className="absolute inset-y-0 right-0 max-w-2xl w-full bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Lead Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Status Badges */}
          <div className="flex gap-2 mb-6">
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getClassificationColor(lead.classification)}`}>
              {lead.classification.toUpperCase()}
            </span>
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(lead.status)}`}>
              {lead.status.toUpperCase()}
            </span>
            <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
              {lead.confidence_score.toFixed(1)}% Confidence
            </span>
          </div>

          {/* Original Message */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {lead.original_language && lead.original_language !== 'en'
                ? `Original Message (${getLanguageName(lead.original_language)})`
                : 'Original Message'}
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{getPlatformIcon(lead.source_platform)}</span>
                <span className="text-sm font-medium text-gray-900">
                  {lead.source_platform.toUpperCase()}
                </span>
                {lead.source_url && (
                  <a
                    href={lead.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    View Source →
                  </a>
                )}
              </div>
              <p className="text-gray-900 whitespace-pre-wrap">
                {lead.original_content}
              </p>
            </div>
          </div>

          {/* English Translation (if original is not in English) */}
          {lead.english_translation && lead.original_language && lead.original_language !== 'en' && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <span>English Translation</span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                  Auto-translated
                </span>
              </h3>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {lead.english_translation}
                </p>
              </div>
            </div>
          )}

          {/* Extracted Information */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Extracted Information
            </h3>
            <div className="space-y-3">
              {lead.name && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="text-sm font-medium text-gray-900">{lead.name}</span>
                </div>
              )}

              {(lead.contact_info || lead.source_url || isEnriching) && (
                <div className={`rounded-lg transition-all ${flash ? 'ring-2 ring-emerald-300 bg-emerald-50/40 -mx-2 px-2 py-1' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Contact</span>
                    {lead.classification === 'qualified' && !isEnriching && (
                      <button
                        onClick={handleGetContact}
                        disabled={enriching}
                        title="Find contact details from the author's profile, website and post (AI-assisted)"
                        className="text-xs text-purple-600 hover:text-purple-700 disabled:opacity-50"
                      >
                        {enriching ? 'Starting…' : lead.enrichment_status === 'done' ? '↻ Re-fetch' : '✨ Get Contact'}
                      </button>
                    )}
                  </div>

                  {isEnriching ? (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span className="capitalize">{lead.enrichment_stage || 'queued'}…</span>
                        <span>{stagePct}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-blue-500 transition-all duration-700" style={{ width: `${stagePct}%` }} />
                      </div>
                      <div className="mt-3 space-y-2 animate-pulse">
                        <div className="h-3 bg-gray-100 rounded w-2/3" />
                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                        <div className="h-3 bg-gray-100 rounded w-3/4" />
                      </div>
                      <p className="mt-2 text-xs text-gray-400">Finding contact details in the background — you can switch leads; this keeps running.</p>
                    </div>
                  ) : (
                    <div className="mt-1 space-y-1">
                      {lead.contact_info?.email && (
                        <div className="text-sm text-gray-900">
                          📧 <a href={`mailto:${lead.contact_info.email}`} className="text-blue-600 hover:underline">{lead.contact_info.email}</a>
                        </div>
                      )}
                      {lead.contact_info?.whatsapp && (
                        <div className="text-sm text-gray-900">
                          💬 <a href={`https://wa.me/${lead.contact_info.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">WhatsApp</a>
                        </div>
                      )}
                      {lead.contact_info?.phone && (
                        <div className="text-sm text-gray-900">
                          📱 <a href={`tel:${lead.contact_info.phone}`} className="text-blue-600 hover:underline">{lead.contact_info.phone}</a>
                        </div>
                      )}
                      {lead.contact_info?.profile_url && (
                        <div className="text-sm text-gray-900">
                          👤 <a href={lead.contact_info.profile_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View profile</a>
                        </div>
                      )}
                      {lead.contact_info?.website && (
                        <div className="text-sm text-gray-900">
                          🌐 <a href={lead.contact_info.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{lead.contact_info.website}</a>
                        </div>
                      )}
                      {lead.contact_info?.social_media && (
                        <div className="text-sm text-gray-900">🔗 {lead.contact_info.social_media}</div>
                      )}
                      {lead.contact_info?.social_handles && Object.keys(lead.contact_info.social_handles).length > 0 && (
                        <div className="text-sm text-gray-900">
                          <span className="text-gray-600">Other profiles:</span>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {Object.entries(lead.contact_info.social_handles).map(([site, val]) => {
                              const href = val.startsWith('http') ? val : undefined;
                              return href ? (
                                <a key={site} href={href} target="_blank" rel="noopener noreferrer"
                                   className="px-2 py-0.5 bg-gray-100 rounded text-blue-600 hover:underline capitalize">
                                  {site}
                                </a>
                              ) : (
                                <span key={site} className="px-2 py-0.5 bg-gray-100 rounded capitalize">{site}: {val}</span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {(lead.contact_info?.company || lead.contact_info?.role) && (
                        <div className="text-sm text-gray-900">
                          💼 {[lead.contact_info.role, lead.contact_info.company].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {(lead.contact_info?.followers != null || lead.contact_info?.verified) && (
                        <div className="text-sm text-gray-600">
                          {lead.contact_info?.verified && <span className="text-blue-600">✔ Verified</span>}
                          {lead.contact_info?.verified && lead.contact_info?.followers != null && ' · '}
                          {lead.contact_info?.followers != null && `${lead.contact_info.followers.toLocaleString()} followers`}
                        </div>
                      )}
                      {!lead.contact_info && lead.source_url && (
                        <div className="text-sm text-gray-900">
                          🔗 Contact via{' '}
                          <a href={lead.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {lead.source_platform} post
                          </a>
                        </div>
                      )}
                      {lead.enrichment_status === 'done' && lead.enriched_at && (
                        <p className="text-xs text-gray-400 pt-1">Updated {formatDate(lead.enriched_at)}</p>
                      )}
                      {lead.enrichment_status === 'failed' && (
                        <p className="text-xs text-red-500 pt-1">Enrichment failed — try Re-fetch.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {lead.intent_type && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Intent Type:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{lead.intent_type.replace('_', ' ')}</span>
                </div>
              )}

              {lead.target_location && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Target Location:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {[lead.target_location.city, lead.target_location.neighborhood, lead.target_location.country]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}

              {lead.stay_duration && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Stay Duration:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{lead.stay_duration.replace('_', ' ')}</span>
                </div>
              )}

              {lead.budget_range && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Budget Range:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {lead.budget_range.currency} {lead.budget_range.min?.toLocaleString()} - {lead.budget_range.max?.toLocaleString()}
                  </span>
                </div>
              )}

              {lead.timeline && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Timeline:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{lead.timeline.replace('_', ' ')}</span>
                </div>
              )}

              {lead.property_preferences && Object.keys(lead.property_preferences).length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 block mb-2">Property Preferences:</span>
                  <div className="bg-gray-50 rounded p-2 border border-gray-200">
                    {Object.entries(lead.property_preferences).map(([key, value]) => (
                      <div key={key} className="text-sm text-gray-900">
                        <span className="font-medium capitalize">{key.replace('_', ' ')}:</span> {String(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {lead.special_requirements && lead.special_requirements.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 block mb-2">Special Requirements:</span>
                  <div className="flex flex-wrap gap-2">
                    {lead.special_requirements.map((req, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {req}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Metadata
            </h3>
            <div className="space-y-2 text-xs text-gray-600">
              <div>
                <span className="font-medium">Lead ID:</span> {lead.id}
              </div>
              <div>
                <span className="font-medium">External ID:</span> {lead.external_id}
              </div>
              <div>
                <span className="font-medium">Detected At:</span> {formatDate(lead.detected_at)}
              </div>
              <div>
                <span className="font-medium">Created At:</span> {formatDate(lead.created_at)}
              </div>
              {lead.created_by && (
                <div>
                  <span className="font-medium">Created By:</span> {lead.created_by}
                </div>
              )}
              {lead.updated_at !== lead.created_at && (
                <div>
                  <span className="font-medium">Last Updated:</span> {formatDate(lead.updated_at)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex gap-3">
            {lead.status !== 'rejected' && (
              <>
                <button
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  onClick={() => alert('Contact feature coming soon!')}
                >
                  Contact Lead
                </button>
                <button
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${
                    lead.slack_notification_sent
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                  onClick={handleSendToMattermost}
                  title={lead.slack_notification_sent ? "Already notified - click to resend" : "Send notification to Mattermost"}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.32 14.617l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.496.969z"/>
                  </svg>
                  {lead.slack_notification_sent ? 'Notified' : 'Send to Mattermost'}
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  onClick={handleReject}
                >
                  Reject
                </button>
              </>
            )}
            <button
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailPanel;
